import { ZonedDateTimeFactory, zonedMin } from '@csp/csp-common-date-util';
import { CspError, CustomStatuses, Maybe, StateAssert, ZonedDateTime } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { ScheduleReferenceDates } from '../model/ScheduleReferenceDates';
import { ScheduleWindowInterval } from '../model/ScheduleWindowInterval';
import { GenericRequest } from '../model/schedulingModels/GenericRequest';
import { RequestWhile } from '../model/schedulingModels/RequestWhile';
import { RequestWhileCriterion } from '../model/schedulingModels/RequestWhileCriterion';
import { RequestWhileCriterionType } from '../model/schedulingModels/RequestWhileCriterionType';
import { RequestWhileOperator } from '../model/schedulingModels/RequestWhileOperator';
import { isWindowActiveNow } from '../util/scheduleUtil';
import { DemographicValues } from '../mapper/DemographicValuesMapper';
import { ScheduleIntervalService } from './ScheduleIntervalService';

const evaluateInvertedWindowIntervals = (windowIntervals: ScheduleWindowInterval[]): ScheduleWindowInterval[] => {
  if (!windowIntervals.length) {
    return [ScheduleWindowInterval.from(ZonedDateTimeFactory.fromUnixTimeMillis(0), undefined)]; // An open window from time zero
  } else {
    const invertedIntervals: ScheduleWindowInterval[] = [
      ScheduleWindowInterval.from(ZonedDateTimeFactory.fromUnixTimeMillis(0), windowIntervals[0]?.windowStart), // A window from time zero closing when first window starts
    ];

    for (let idx = 0; idx < windowIntervals.length; idx++) {
      const interval = windowIntervals[idx];
      const nextInterval = windowIntervals[idx + 1];
      if (interval?.windowEnd) {
        invertedIntervals.push(ScheduleWindowInterval.from(interval.windowEnd, nextInterval?.windowStart));
      }
    }
    return invertedIntervals;
  }
};

const isMatchingCriterion = (criterion: RequestWhileCriterion, value: string): boolean => {
  const operator = criterion.operator;

  switch (operator) {
    case RequestWhileOperator.IN:
      return criterion.values.includes(value);
    case RequestWhileOperator.NOT_IN:
      return !criterion.values.includes(value);
    case RequestWhileOperator.GREATER_THAN: {
      const criterionValueNumber = Number(criterion.values[0]);
      StateAssert.isFalse(
        isNaN(criterionValueNumber),
        `Value used with while criteria ${RequestWhileOperator.GREATER_THAN} is not a number: ${criterion.values[0]}`,
      );
      return Number(value) > criterionValueNumber;
    }
    case RequestWhileOperator.GREATER_THAN_EQ: {
      const criterionValueNumber = Number(criterion.values[0]);
      StateAssert.isFalse(
        isNaN(criterionValueNumber),
        `Value used with while criteria ${RequestWhileOperator.GREATER_THAN_EQ} is not a number: ${criterion.values[0]}`,
      );
      return Number(value) >= criterionValueNumber;
    }
    case RequestWhileOperator.LESS_THAN: {
      const criterionValueNumber = Number(criterion.values[0]);
      StateAssert.isFalse(
        isNaN(criterionValueNumber),
        `Value used with while criteria ${RequestWhileOperator.LESS_THAN} is not a number: ${criterion.values[0]}`,
      );
      return Number(value) < criterionValueNumber;
    }
    case RequestWhileOperator.LESS_THAN_EQ: {
      const criterionValueNumber = Number(criterion.values[0]);
      StateAssert.isFalse(
        isNaN(criterionValueNumber),
        `Value used with while criteria ${RequestWhileOperator.LESS_THAN_EQ} is not a number: ${criterion.values[0]}`,
      );
      return Number(value) <= criterionValueNumber;
    }
    default:
      return false;
  }
};

const getCustomStatusIntervals = (
  criterion: RequestWhileCriterion,
  userCustomStatuses: Maybe<CustomStatuses>,
): ScheduleWindowInterval[] => {
  const customStatusValues =
    userCustomStatuses?.getByType(criterion.subType)?.getStatusValuesInSequenceOldestFirstIncludingInvalid() ?? []; // This one is important for PAUSE functionality..

  const scheduleWindowIntervals = customStatusValues
    .reduce<ScheduleWindowInterval[]>((intervals, status, index, statuses) => {
      const isMatchingStatusValue = isMatchingCriterion(criterion, status.value);

      if (isMatchingStatusValue) {
        const nextStatus = statuses[index + 1];
        const intervalEnd = zonedMin([nextStatus?.timestamp, status.invalidationDate].filter(isDefined));
        return [...intervals, ScheduleWindowInterval.from(status.timestamp, intervalEnd)];
      } else {
        return intervals;
      }
    }, [])
    .filter(interval => ScheduleIntervalService.durationMillis(interval) > 0);

  if (criterion.operator === RequestWhileOperator.NOT_IN) {
    /**
     * Adds interval from time zero to ensure that NOT_IN criteria is valid before any statuses are set.
     */
    scheduleWindowIntervals.unshift(
      ScheduleWindowInterval.from(ZonedDateTimeFactory.fromUnixTimeMillis(0), customStatusValues[0]?.localTimestamp),
    );
  }

  return ScheduleIntervalService.union(scheduleWindowIntervals);
};

const getDemographicValuesIntervals = (
  criterion: RequestWhileCriterion,
  userDemographicValues: Maybe<DemographicValues>,
): ScheduleWindowInterval[] => {
  const userDemographicValuesByType = userDemographicValues?.getByTypeOldestFirst(criterion.subType) ?? [];

  const scheduleWindowIntervals = userDemographicValuesByType.reduce<ScheduleWindowInterval[]>(
    (intervals, demographicValue, index, demographicValues) => {
      const nextValue = demographicValues[index + 1];
      const isMatching = demographicValue.value && isMatchingCriterion(criterion, demographicValue.value);

      return isMatching
        ? [...intervals, ScheduleWindowInterval.from(demographicValue.createdAt, nextValue?.createdAt)]
        : intervals;
    },
    [],
  );

  return ScheduleIntervalService.union(scheduleWindowIntervals);
};

const calculateIntervalsForCriterion = (
  criterion: RequestWhileCriterion,
  scheduleReferenceDates: ScheduleReferenceDates,
): ScheduleWindowInterval[] => {
  if (criterion.type === RequestWhileCriterionType.USER_CUSTOM_STATUS) {
    return getCustomStatusIntervals(criterion, scheduleReferenceDates.userCustomStatuses);
  } else if (criterion.type === RequestWhileCriterionType.USER_DEMOGRAPHIC_VALUE) {
    return getDemographicValuesIntervals(criterion, scheduleReferenceDates.userDemographicValues);
  } else {
    throw CspError.badState(`Unsupported while criterion type: ${criterion.type}`);
  }
};

const getIntervalIntersections = (intervals: ScheduleWindowInterval[][]): ScheduleWindowInterval[] => {
  if (intervals.length <= 1) {
    // Nothing to intersect, just return the first list of intervals
    return intervals[0] ?? [];
  } else {
    let result = intervals.pop() ?? [];
    for (const interval of intervals) {
      result = ScheduleIntervalService.intersectionOfIntervalLists(result, interval);
    }
    return result;
  }
};

const getMatchingIntervals = (
  requestWhile: RequestWhile,
  scheduleReferenceDates: ScheduleReferenceDates,
): ScheduleWindowInterval[] => {
  const criteria = requestWhile.criteria ?? [];

  const intervalsPerCriteria = criteria.map(criterion =>
    calculateIntervalsForCriterion(criterion, scheduleReferenceDates),
  );

  // Implicit AND operator, should be union if OR is added
  return getIntervalIntersections(intervalsPerCriteria);
};

/**
 * Whether the input interval overlaps with a matching "while criteria"-interval.
 * Can be used to determine whether the user has had a chance to complete the task.
 */
const isOverlappingWithMatchingInterval = (
  request: GenericRequest,
  scheduleReferenceDates: ScheduleReferenceDates,
  intervalIn: ScheduleWindowInterval,
): boolean => {
  if (request.while) {
    const intervals = getMatchingIntervals(request.while, scheduleReferenceDates);
    return intervals.some(interval => ScheduleIntervalService.intersection(interval, intervalIn));
  } else {
    return true;
  }
};

/**
 * Whether the input interval is fully contained by a matching "while criteria"-interval.
 * Can be used to determine if the user has had the entire request window to
 * complete the task.
 */
const isContainedWithinMatchingInterval = (
  request: GenericRequest,
  scheduleReferenceDates: ScheduleReferenceDates,
  intervalIn: ScheduleWindowInterval,
): boolean => {
  if (request.while) {
    const matchingIntervals = getMatchingIntervals(request.while, scheduleReferenceDates);
    const nonMatchingIntervals = evaluateInvertedWindowIntervals(matchingIntervals);
    return !nonMatchingIntervals.some(interval => ScheduleIntervalService.intersection(interval, intervalIn));
  } else {
    // If no while config is defined the request is
    // always applicable for the patient, hence considered within matching interval.
    // while config only affects the request when defined.
    return true;
  }
};

const isWithinMatchingIntervalNow = (
  request: GenericRequest,
  scheduleReferenceDates: ScheduleReferenceDates,
  now: ZonedDateTime = ZonedDateTimeFactory.now(),
): boolean => {
  if (request.while) {
    const intervals = getMatchingIntervals(request.while, scheduleReferenceDates);
    return intervals.some(interval => isWindowActiveNow(interval, now));
  } else {
    return true;
  }
};

export const RequestWhileService = {
  getMatchingIntervals,
  isOverlappingWithMatchingInterval,
  isContainedWithinMatchingInterval,
  isWithinMatchingIntervalNow,
};
